import { z } from 'zod'
import {
  EndDate,
  Id,
  IsActive,
  NextId,
  ObjectId,
  PrevId,
  StartDate,
  UpdateDate,
} from './common-schemas'
import { GarItem } from './gar-item'

export namespace MunHierarchyItem {
  export interface Data {
    /** @description Unique record identifier. Key field */
    readonly id: number

    /** @description Global unique identifier of the address object */
    readonly objectId: number

    /** @description Parent object identifier */
    readonly parentObjId?: number

    /** @description Identifier of the changing transaction */
    readonly changeId: number

    /** @description OKTMO code */
    readonly oktmo?: string

    /** @description Identifier of the record linking to the previous historical record */
    readonly prevId?: number

    /** @description Identifier of the record linking to the subsequent historical record */
    readonly nextId?: number

    /** @description Record entry (update) date */
    readonly updateDate: Date

    /** @description Record validity start */
    readonly startDate: Date

    /** @description Record validity end */
    readonly endDate: Date

    /** @description Active address object indicator */
    readonly isActive: boolean

    /** @description Materialized path to the object (full hierarchy) */
    readonly path: string
  }
}

/** @description Information on hierarchy in municipal division */
export class MunHierarchyItem extends GarItem<
  'MunHierarchyItem',
  MunHierarchyItem.Data
> {
  static readonly SCHEMA: GarItem.Schema<MunHierarchyItem.Data> = z
    .strictObject({
      ID: Id,

      OBJECTID: ObjectId,

      PARENTOBJID: z.coerce
        .number<string>()
        .int()
        .optional()
        .describe('Parent object identifier'),

      CHANGEID: z.coerce
        .number<string>()
        .int()
        .describe('Identifier of the changing transaction'),

      OKTMO: z.string().describe('OKTMO code').optional(),

      PREVID: PrevId.optional(),

      NEXTID: NextId.optional(),

      UPDATEDATE: UpdateDate,

      STARTDATE: StartDate,

      ENDDATE: EndDate,

      ISACTIVE: IsActive,

      PATH: z
        .string()
        .describe('Materialized path to the object (full hierarchy)'),
    })
    .transform<MunHierarchyItem.Data>(attributes => ({
      id: attributes.ID,
      objectId: attributes.OBJECTID,
      parentObjId: attributes.PARENTOBJID,
      changeId: attributes.CHANGEID,
      oktmo: attributes.OKTMO,
      prevId: attributes.PREVID,
      nextId: attributes.NEXTID,
      updateDate: attributes.UPDATEDATE,
      startDate: attributes.STARTDATE,
      endDate: attributes.ENDDATE,
      isActive: attributes.ISACTIVE,
      path: attributes.PATH,
    }))

  readonly type = 'MunHierarchyItem'

  protected get schema() {
    return MunHierarchyItem.SCHEMA
  }
}
